import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useAppSelector } from 'app/hooks';
import { MeasurementUnitEnum } from 'domains/measurementDomain';

interface MeasurementUnitScopeValue {
  /** The unit this form reads and writes in. */
  unit: MeasurementUnitEnum;
  /** Switches this form - and nothing outside it - to another unit. */
  setUnit: (unit: MeasurementUnitEnum) => void;
}

const MeasurementUnitScopeContext = createContext<
  MeasurementUnitScopeValue | undefined
>(undefined);

/** The enclosing form's unit, or `undefined` outside any form. */
export const useMeasurementUnitScope = () =>
  useContext(MeasurementUnitScopeContext);

type Props = {
  /**
   * What the form starts in.
   *
   * Defaults to what the reader is already working in - their profile setting, or the org's
   * default where they have none - so opening a form and saving it without touching the selector
   * behaves exactly as it did before this control existed.
   */
  defaultUnit?: MeasurementUnitEnum;
  children: ReactNode;
};

/**
 * Gives one form its own measurement unit, shown and chosen on the form itself.
 *
 * Everything inside the provider - `useMeasurementUnit` and therefore every input, column header
 * and size picker under it - follows this unit instead of the reader's. That includes what the
 * form declares on save (`enteredUnit`), so the numbers and the declaration cannot disagree.
 * Nothing outside it changes, so list and detail pages keep rendering in the reader's own unit.
 *
 * A multi-step wizard wraps all of its steps in one scope, so the unit is picked once in the
 * header rather than on each step.
 *
 * Until the user picks, the scope tracks the reader's unit rather than snapshotting it: the org
 * default is settled by an API call during session hydration, and a form mounted before that
 * answered would otherwise be stuck in imperial while the rest of the app moved to metric.
 */
export const MeasurementUnitScope = ({ defaultUnit, children }: Props) => {
  const readerUnit = useAppSelector(
    (state) => state.app.measurementUnit ?? MeasurementUnitEnum.IMPERIAL
  );
  const [picked, setPicked] = useState<MeasurementUnitEnum>();

  const value = useMemo(
    () => ({
      unit: picked ?? defaultUnit ?? readerUnit,
      setUnit: setPicked,
    }),
    [picked, defaultUnit, readerUnit]
  );

  return (
    <MeasurementUnitScopeContext.Provider value={value}>
      {children}
    </MeasurementUnitScopeContext.Provider>
  );
};

/**
 * Wraps a component in its own measurement-unit scope.
 *
 * The selector has to live in the same subtree as the fields it governs, and these forms render
 * their own header - so the provider cannot be added at the call site without touching every
 * place the form is opened from.
 */
export const withMeasurementUnitScope = <P extends object>(
  Component: React.ComponentType<P>,
  defaultUnitOf?: (props: P) => MeasurementUnitEnum | undefined
) => {
  const Wrapped = (props: P) => (
    <MeasurementUnitScope defaultUnit={defaultUnitOf?.(props)}>
      <Component {...props} />
    </MeasurementUnitScope>
  );
  Wrapped.displayName = `withMeasurementUnitScope(${
    Component.displayName || Component.name || 'Component'
  })`;
  return Wrapped;
};
